import { useSelector, useDispatch } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import {
  Stack,
  Typography,
  ProgressBar,
  Tooltip,
  IconButton,
  Button,
  Divider,
  Ul,
  UlItem,
} from "@financeable-com-au/financeable-ui";
// @ts-expect-error: ignoring as it is existing js file
import { applicationSelector } from "@/store/slices/applicationFormSlice";
import { type ApplicationFormSlice, type ApplicationState } from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userPermissionsSelector } from "@/store/slices/userSlice";
import type { UserSlice, UserPermissionsDocument } from "@/store/slices/types/userSlice";
import { lenderCardsSelector, setLenderCardsTooltip, resetLenderCardsTooltip } from "@/store/slices/lenderCards";
import { useRequiredFieldsLabel, useShowRequiredFields } from "@/hooks/useRequiredFields";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";
// @ts-expect-error: ignoring as it is existing js file
import { getCustomerRole } from "@/components/Utils/helperFunction";

const FieldProgress = () => {
  const { product, requiredFields } = useLenderCardContext();
  const {
    remainingFields,
    fieldsTotalLength,
    fieldsCheckedPercentage,
    fieldsDoneLength,
    fieldsStatus,
    isFieldsCompleted,
  } = requiredFields ?? {};
  const permissions = useSelector<UserSlice, UserPermissionsDocument>(userPermissionsSelector);
  const application = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const { getSimpleFieldsLabel, getComplexFieldsLabel } = useRequiredFieldsLabel();

  // Tooltip is open if the lenderId matches the product.lenderId and the type is requiredFields.
  const { tooltip } = useSelector(lenderCardsSelector);
  const dispatch = useDispatch();
  const isTooltipOpen =
    tooltip !== undefined && tooltip.lenderId === product?.lenderId && tooltip.type === "requiredFields";
  const handleClickTooltip = () => {
    if (isTooltipOpen) {
      dispatch(resetLenderCardsTooltip());
    } else {
      dispatch(
        setLenderCardsTooltip({
          lenderId: product?.lenderId,
          isOpen: true,
          type: "requiredFields",
        }),
      );
    }
  };
  const handleCloseTooltip = () => {
    dispatch(resetLenderCardsTooltip());
  };

  const assetFields = getSimpleFieldsLabel(remainingFields?.asset ?? []);
  const customerFields = getComplexFieldsLabel(remainingFields?.customer ?? []);
  const entityFields = getComplexFieldsLabel(remainingFields?.entity ?? []);
  const agreementFields = getSimpleFieldsLabel(remainingFields?.agreement ?? []);
  const loanDetailsFields = getSimpleFieldsLabel(remainingFields?.loanDetails ?? []);
  const isDisableEdit = !permissions?.application?.edit || !application?.canEditApplication;

  // Show required fields.
  const { showRequiredFields } = useShowRequiredFields();

  return (
    <Stack direction="row" alignItems="center" columnGap={2} maxWidth="350px">
      <Typography level="body-xs">Required Fields</Typography>
      <ProgressBar value={fieldsCheckedPercentage} status={fieldsStatus} />
      <Typography level="body-xs" bold={true}>
        {fieldsDoneLength}/{fieldsTotalLength}
      </Typography>
      {/* Tooltip of remaining required fields. */}
      <Tooltip
        open={isTooltipOpen}
        title={
          <Stack padding={2} flexDirection="column" rowGap={2}>
            <Typography level="body-sm" bold>
              {product?.lender ? `${product.lender}'s remaining required fields` : "Remaining required fields"}
            </Typography>
            <Divider />
            {!isFieldsCompleted ? (
              <>
                <Stack sx={{ overflow: "auto", paddingRight: 2 }} maxHeight="200px" flexDirection="column" rowGap={3}>
                  <>
                    {assetFields.length > 0 && (
                      <div>
                        <>
                          <Typography bold mb={1}>
                            Asset:
                          </Typography>
                          <Ul lineHeight={1.2}>
                            {assetFields.map((field, index) => (
                              <UlItem key={`${field.fieldName}-${index}`}>{field.fieldLabel}</UlItem>
                            ))}
                          </Ul>
                        </>
                      </div>
                    )}
                    {customerFields.length > 0 && (
                      <div>
                        <>
                          <Typography bold mb={1}>
                            {getCustomerRole(application.applicationType, application.entities, application.purpose)}:
                          </Typography>
                          <Ul lineHeight={1.2}>
                            {customerFields.map((field, index) => (
                              <UlItem key={`${field.fieldName}-${index}`}>{field.fieldLabel}</UlItem>
                            ))}
                          </Ul>
                        </>
                      </div>
                    )}
                    {entityFields.length > 0 && (
                      <div>
                        <>
                          <Typography bold mb={1}>
                            Entity:
                          </Typography>
                          <Ul lineHeight={1.2}>
                            {entityFields.map((field, index) => (
                              <UlItem key={`${field.fieldName}-${index}`}>{field.fieldLabel}</UlItem>
                            ))}
                          </Ul>
                        </>
                      </div>
                    )}
                    {agreementFields.length > 0 && (
                      <div>
                        <>
                          <Typography bold mb={1}>
                            Agreement:
                          </Typography>
                          <Ul lineHeight={1.2}>
                            {agreementFields.map((field, index) => (
                              <UlItem key={`${field.fieldName}-${index}`}>{field.fieldLabel}</UlItem>
                            ))}
                          </Ul>
                        </>
                      </div>
                    )}
                    {loanDetailsFields.length > 0 && (
                      <div>
                        <Typography bold mb={1}>
                          Loan Details:
                        </Typography>
                        <Ul lineHeight={1.2}>
                          {loanDetailsFields.map((field, index) => (
                            <UlItem key={`${field.fieldName}-${index}`}>{field.fieldLabel}</UlItem>
                          ))}
                        </Ul>
                      </div>
                    )}
                  </>
                </Stack>
                <Button
                  onClick={() => {
                    showRequiredFields({
                      asset: assetFields,
                      customer: customerFields,
                      entity: entityFields,
                      agreement: agreementFields,
                      loanDetails: loanDetailsFields,
                    });
                  }}
                  disabled={isDisableEdit}
                  variant="primary"
                  fullWidth
                >
                  Show Fields
                </Button>
              </>
            ) : (
              <Typography level="body-sm">Well done, you have completed all required fields!</Typography>
            )}
            <Button onClick={handleCloseTooltip} variant="secondary" fullWidth>
              Close Panel
            </Button>
          </Stack>
        }
      >
        <IconButton
          aria-label="Tooltip"
          isRounded={true}
          size="sm"
          variant="inline"
          intent="neutral"
          onClick={handleClickTooltip}
        >
          <InfoIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export { FieldProgress };
